import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import * as mentorApi from '../../api/mentorApi';

const REASON_MAX = 500;

// Mark a member as dropped out of a squad (T5). The reason is required and kept. A squad that falls under 3 active members
// is paused and the directorate is emailed; the dialog says so before staff confirm. The server refuses when the member
// still chairs a mission that has not started and says what to do first.
// Rendered into document.body, like the other ESG dialogs, because the screens sit inside a transformed container.
export default function EsgDropOutModal({ squad, member, onClose, onChanged, onSuccess }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const activeAfter = (squad.members || []).filter((m) => m.status !== 'removed').length - 1;
  const willPause = activeAfter < 3;
  const name = member.student?.name || 'This member';

  const save = async () => {
    setBusy(true);
    setError('');
    try {
      const updated = await mentorApi.dropOutEsgMember(squad.id, member.student_id, reason.trim());
      onSuccess?.(`${name} was marked as dropped out.`);
      onChanged?.(updated);
      onClose();
    } catch (err) {
      setError(err?.data?.message || "Couldn't mark the drop-out - please try again.");
      setBusy(false);
    }
  };

  return createPortal(
    <div className="modal-overlay" onClick={busy ? undefined : onClose}>
      <div
        className="modal-card esg-modal-card esg-modal-card--md"
        role="dialog"
        aria-modal="true"
        aria-labelledby="esg-dropout-title"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="esg-dropout-title" className="esg-modal-title">{`Mark ${name} as dropped out`}</h3>
        <p className="esg-modal-sub">
          {`${squad.name} — ${activeAfter} active member${activeAfter === 1 ? '' : 's'} would be left.`}
        </p>

        {willPause && (
          <div className="rejection-banner esg-mb-14" role="status">
            {activeAfter >= 2
              ? 'A squad needs 3 members to complete the level. With 2 it is paused, the directorate is emailed, and it can only continue once an auditor or a program administrator allows it.'
              : 'A squad with fewer than 2 members cannot continue. It will be paused and the directorate is emailed.'}
          </div>
        )}

        <label htmlFor="esg-dropout-reason" className="esg-modal-label">Reason</label>
        <textarea
          id="esg-dropout-reason"
          className="form-control-select esg-textarea"
          maxLength={REASON_MAX}
          value={reason}
          disabled={busy}
          onChange={(e) => { setError(''); setReason(e.target.value); }}
        />
        <div className="esg-modal-hint">{`${reason.length}/${REASON_MAX}`}</div>

        {error && <div className="rejection-banner esg-mt-12" role="alert">{error}</div>}

        <div className="esg-modal-actions">
          <button type="button" className="esg-btn esg-btn-secondary" disabled={busy} onClick={onClose}>Cancel</button>
          <button type="button" className="esg-btn esg-btn-primary" disabled={reason.trim().length < 3 || busy} onClick={save}>
            {busy ? 'Saving...' : 'Mark as dropped out'}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
